/**
 * Anzeige-Helfer für KIAN-Aktionskarten: Texte, Farben, Links. Ohne React, damit die Karte,
 * die Seitenleiste des KIAN-Fensters und Tests dieselben Formulierungen nutzen.
 */
import type {
  KianAction,
  KianActionItem,
  KianActionItemStatus,
  KianActionStatus,
  KianTicketDraft,
  KianValueMapField,
} from '../../../lib/api/domains/kianActions';
import type { PortalTechnician } from '../../../lib/api/domains/ticketHandover';

export const ACTION_STATUS_LABEL: Record<KianActionStatus, string> = {
  PROPOSED: 'Entwurf',
  RUNNING: 'Läuft',
  DONE: 'Erledigt',
  PARTIAL: 'Teilweise erledigt',
  FAILED: 'Fehlgeschlagen',
  DISCARDED: 'Verworfen',
};

export const ACTION_STATUS_COLOR: Record<KianActionStatus, string> = {
  PROPOSED: 'cyberOrange',
  RUNNING: 'blue',
  DONE: 'green',
  PARTIAL: 'yellow',
  FAILED: 'red',
  DISCARDED: 'gray',
};

export const ITEM_STATUS_LABEL: Record<KianActionItemStatus, string> = {
  PENDING: 'offen',
  RUNNING: 'läuft',
  DONE: 'erledigt',
  FAILED: 'fehlgeschlagen',
  SKIPPED: 'übersprungen',
  UNKNOWN: 'Ausgang unklar',
  QUEUED: 'wartet auf weclapp',
};

/**
 * Geparkter Eintrag (weclapp war nicht erreichbar): wie steht es in den Wartelisten? Ohne Angabe
 * gilt „wartet" — die Karte kennt den Stand dann nur noch nicht.
 */
export function queueStateOf(item: Pick<KianActionItem, 'status' | 'queue'>): 'WAITING' | 'SYNCED' | 'STUCK' | null {
  if (item.status !== 'QUEUED') return null;
  return item.queue?.state ?? 'WAITING';
}

export const QUEUE_STATE_TEXT: Record<'WAITING' | 'SYNCED' | 'STUCK', string> = {
  WAITING:
    'weclapp war nicht erreichbar — liegt in der Warteliste und geht automatisch nach, sobald weclapp wieder antwortet (Kopfzeile: „wartet auf Weclapp").',
  SYNCED: 'Nachgereicht — steht jetzt in weclapp.',
  STUCK: 'Beim Nachreichen abgelehnt — in der Kopfzeile unter „nicht übertragen" nachsehen.',
};

/** Wartet die Neuanlage selbst, stimmt „weclapp war nicht erreichbar" nicht immer — das Ticket ist ja da. */
export const QUEUE_TICKET_WAITING_TEXT =
  'Angelegt im ServiceHub — weclapp übernimmt das Ticket automatisch, Kommentare und Status folgen danach.';

export function queueStateText(item: Pick<KianActionItem, 'status' | 'queue'>): string | null {
  const queue = queueStateOf(item);
  if (!queue) return null;
  return queue === 'WAITING' && item.queue?.ticket ? QUEUE_TICKET_WAITING_TEXT : QUEUE_STATE_TEXT[queue];
}

export const VALUE_MAP_FIELD_LABEL: Record<KianValueMapField, string> = {
  customer: 'Kunde',
  status: 'Status',
  priority: 'Priorität',
  category: 'Kategorie',
  assignee: 'Bearbeiter',
};

/** Ein Entwurf ist änderbar, solange er nicht läuft oder verworfen ist. */
export function isActionEditable(action: Pick<KianAction, 'status'>): boolean {
  return action.status === 'PROPOSED' || action.status === 'PARTIAL' || action.status === 'FAILED' || action.status === 'DONE';
}

/**
 * Einträge, die (noch) geändert werden dürfen. Steht ein neues Ticket schon in weclapp, änderte
 * ein neuer Entwurf nichts mehr daran — „Erneut versuchen" setzt nur fort (wie der Server).
 */
export function isItemEditable(item: Pick<KianActionItem, 'status' | 'ticketId'>, kind?: KianAction['kind']): boolean {
  if (kind === 'TICKET_CREATE' && item.ticketId) return item.status === 'FAILED';
  return item.status === 'PENDING' || item.status === 'FAILED';
}

/**
 * Anlage gescheitert, das Ticket steht aber schon: änderbar ist nur noch, was aussteht —
 * Bearbeiter und Status (Server: resumeDraft.ts). Bis v1.389.x war der Eintrag ganz gesperrt, und
 * ein von weclapp abgelehnter Bearbeiter („weclapp Support") scheiterte bei jedem Versuch erneut.
 */
export function isResumeOnly(item: Pick<KianActionItem, 'status' | 'ticketId'>, kind?: KianAction['kind']): boolean {
  return kind === 'TICKET_CREATE' && !!item.ticketId && item.status === 'FAILED';
}

/** Sammelbearbeitung für einen fortzusetzenden Eintrag: nur Bearbeiter und Status. */
export function resumeBulkChanges(c: KianBulkChanges): KianBulkChanges {
  return {
    ...(c.statusId ? { statusId: c.statusId } : {}),
    ...(c.assignee && c.assignee !== 'pool' ? { assignee: c.assignee } : {}),
    ...(c.assignReason ? { assignReason: c.assignReason } : {}),
  };
}

/** Erledigt aus Sicht des Vorgangs: geschrieben — oder geparkt, den Rest trägt der Nachsync nach. */
export function isItemFinished(item: Pick<KianActionItem, 'status'>): boolean {
  return item.status === 'DONE' || item.status === 'QUEUED';
}

/** Link ins Ticket-Cockpit (Nummer bevorzugt — die Deep-Link-Seite nimmt beides). */
export function ticketHref(item: Pick<KianActionItem, 'ticketNumber' | 'ticketId'>): string | null {
  const ref = item.ticketNumber || item.ticketId;
  return ref ? `/t/${encodeURIComponent(ref)}` : null;
}

export function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString('de-DE')} ${n === 1 ? one : many}`;
}

/** Beschriftung des Hauptknopfs. */
export function executeLabel(action: KianAction, readyCount: number): string {
  if (action.kind === 'KNOWLEDGE_ARTICLE') return 'Zur Freigabe einreichen';
  const create = action.kind === 'TICKET_CREATE';
  if (readyCount <= 1) return create ? 'Ticket anlegen' : 'Änderung ausführen';
  return create ? `${readyCount.toLocaleString('de-DE')} Tickets anlegen` : `${readyCount.toLocaleString('de-DE')} Änderungen ausführen`;
}

/** Anleitung (KNOWLEDGE_ARTICLE): landet in der Wissensdatenbank, nicht in weclapp. */
function knowledgeSubtitle(action: KianAction): string {
  switch (action.status) {
    case 'PROPOSED':
      return action.counts.blocked > 0
        ? 'Entwurf — eine Angabe fehlt noch'
        : 'Entwurf — noch nicht eingereicht. Prüfen, verbessern, dann zur Freigabe einreichen.';
    case 'RUNNING':
      return 'Wird eingereicht …';
    case 'DONE':
      return 'Eingereicht — wartet auf Freigabe durch einen Admin';
    case 'PARTIAL':
    case 'FAILED':
      return 'Nicht eingereicht — Fehler siehe unten';
    case 'DISCARDED':
      return 'Verworfen — nichts eingereicht';
  }
}

/** Eine Zeile Zustand unter dem Titel. */
export function actionSubtitle(action: KianAction): string {
  if (action.kind === 'KNOWLEDGE_ARTICLE') return knowledgeSubtitle(action);
  const c = action.counts;
  const create = action.kind === 'TICKET_CREATE';
  switch (action.status) {
    case 'PROPOSED':
      if (c.blocked > 0) return `Entwurf — ${plural(c.blocked, 'Eintrag braucht', 'Einträge brauchen')} noch eine Entscheidung`;
      return 'Entwurf — noch nichts in weclapp geschrieben. Prüfen und bestätigen.';
    case 'RUNNING':
      return `Wird ausgeführt … ${(c.done + c.queued).toLocaleString('de-DE')} von ${c.included.toLocaleString('de-DE')}`;
    case 'DONE': {
      if (create) return `${plural(c.done, 'Ticket', 'Tickets')} angelegt`;
      // Geparktes, das inzwischen nachgereicht ist, zählt wie Ausgeführtes
      const states = action.items.map(queueStateOf);
      const waiting = states.filter((s) => s === 'WAITING').length;
      const stuck = states.filter((s) => s === 'STUCK').length;
      const written = c.done + states.filter((s) => s === 'SYNCED').length;
      const parts: string[] = [];
      if (written > 0 || (!waiting && !stuck)) parts.push(`${plural(written, 'Änderung', 'Änderungen')} ausgeführt`);
      if (waiting > 0) parts.push(`${waiting.toLocaleString('de-DE')} ${waiting === 1 ? 'wartet' : 'warten'} auf weclapp`);
      if (stuck > 0) parts.push(`${stuck.toLocaleString('de-DE')} beim Nachreichen abgelehnt`);
      return parts.join(' · ');
    }
    case 'PARTIAL':
      return `${(c.done + c.queued).toLocaleString('de-DE')} erledigt · ${c.failed + c.unknown} offen oder fehlgeschlagen`;
    case 'FAILED':
      return 'Nichts ausgeführt — Fehler siehe unten';
    case 'DISCARDED':
      return 'Verworfen — nichts geschrieben';
  }
}

/** Kurzbeschreibung eines Eintrags (Kunde · Zielstatus · Bearbeiter · Kommentare · Blocker). */
export function itemFacts(action: Pick<KianAction, 'kind'>, item: KianActionItem): string[] {
  const r = item.resolved ?? {};
  const d = item.draft ?? {};
  const facts: string[] = [];
  if (action.kind === 'TICKET_UPDATE') {
    if (r.ticket?.partyName) facts.push(r.ticket.partyName);
  } else if (r.customer?.name) {
    facts.push(r.customer.number ? `${r.customer.name} (${r.customer.number})` : r.customer.name);
  } else if (d.customer) {
    facts.push(`„${d.customer}"`);
  }
  if (r.status?.name) facts.push(`→ ${r.status.name}`);
  if (r.priority?.name && action.kind === 'TICKET_UPDATE') facts.push(`Prio ${r.priority.name}`);
  if (r.unassign) facts.push('zurück in den Pool');
  else if (r.assignee?.name) facts.push(r.assignee.isSelf ? 'Bearbeiter: du' : `Bearbeiter: ${r.assignee.name}`);
  if (d.blocker?.waitingFor) facts.push(`⏸ wartet auf ${d.blocker.waitingFor}`);
  if (d.clearBlocker) facts.push('▶ Warten beenden');
  const comments = item.commentsTotal;
  if (comments > 0) {
    facts.push(
      item.status === 'RUNNING' || item.status === 'FAILED' || item.status === 'UNKNOWN'
        ? `Kommentare ${item.commentsDone}/${comments}`
        : plural(comments, 'Kommentar', 'Kommentare'),
    );
  }
  if (r.dueDate) facts.push(`fällig ${r.dueDate.split('-').reverse().join('.')}`);
  return facts;
}

/**
 * Was „Auswahl bearbeiten" auf alle ausgewählten Einträge legt. Nicht gesetzte Felder bleiben,
 * wie sie sind. Werte gehen als ID in den Entwurf — wie im Einzel-Editor gewinnt die ID über den
 * Text aus Chat oder Datei, der Server löst danach alles neu auf.
 */
export interface KianBulkChanges {
  /** nur beim Anlegen — ein Kundenwechsel an bestehenden Tickets bleibt Einzelarbeit */
  customerId?: string | null;
  statusId?: string | null;
  priorityId?: string | null;
  categoryId?: string | null;
  /** weclapp-Benutzer-ID, `self` = der Techniker selbst, `pool` = Bearbeiter entfernen (nur Änderungen) */
  assignee?: string | null;
  assignReason?: string | null;
  dueDate?: string | null;
}

export function hasBulkChanges(c: KianBulkChanges): boolean {
  return !!(c.customerId || c.statusId || c.priorityId || c.categoryId || c.assignee || c.dueDate);
}

export function applyBulkChanges(draft: KianTicketDraft, c: KianBulkChanges): KianTicketDraft {
  const next: KianTicketDraft = { ...draft };
  if (c.customerId && c.customerId !== draft.customerId) {
    // Der Ansprechpartner hing am alten Kunden — sein Name bleibt, der Server sucht ihn beim
    // neuen Kunden; eine alte Kontakt-ID landete sonst am Ticket einer anderen Firma.
    next.customerId = c.customerId;
    next.contactId = null;
  }
  if (c.statusId) next.statusId = c.statusId;
  if (c.priorityId) next.priorityId = c.priorityId;
  if (c.categoryId) next.categoryId = c.categoryId;
  if (c.assignee === 'self') {
    next.assigneeId = null;
    next.assignee = 'ich';
    next.unassign = null;
  } else if (c.assignee === 'pool') {
    next.assigneeId = null;
    next.assignee = null;
    next.unassign = true;
  } else if (c.assignee) {
    next.assigneeId = c.assignee;
    next.unassign = null;
  }
  if (c.assignee && c.assignReason?.trim()) next.assignReason = c.assignReason.trim();
  if (c.dueDate) next.dueDate = c.dueDate;
  return next;
}

/** Einträge eines Entwurfs, die ein Klick auf „Freigeben" jetzt ausführen würde (wie der Server). */
export function runnableItemIds(action: Pick<KianAction, 'items'>): string[] {
  return action.items
    .filter((i) => i.include && (i.status === 'PENDING' || i.status === 'FAILED') && i.ready)
    .map((i) => i.id);
}

/** Aktueller Stand des Tickets, soweit bekannt (Cockpit-Abruf, sonst Kopf aus dem Entwurf). */
export interface KianTicketCurrent {
  subject?: string | null;
  partyId?: string | null;
  partyName?: string | null;
  statusId?: string | null;
  status?: string | null;
  priorityId?: string | null;
  priority?: string | null;
  categoryId?: string | null;
  category?: string | null;
  assigneeId?: string | null;
  assignee?: string | null;
  dueDate?: string | null;
}

/** Eine Zeile der Vorschau: Feld, Stand jetzt (bei Neuanlage `null`), Stand nach dem Ausführen. */
export interface KianChangeRow {
  field: 'customer' | 'subject' | 'status' | 'priority' | 'category' | 'assignee' | 'dueDate';
  label: string;
  before: string | null;
  /** `before` ist ein echter Wert (wird durchgestrichen) — nicht „—" oder „niemand" */
  beforeIsValue: boolean;
  after: string;
}

const NOBODY = 'niemand';

function germanDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/**
 * Was ein Eintrag am Ticket ändert (Änderung) bzw. womit es angelegt wird (Neuanlage) — aus den
 * Werten, die der Server aufgelöst hat, nicht aus dem Chat-Text. Felder, die gleich bleiben,
 * fallen heraus: Die Vorschau soll zeigen, was sich tut, nicht das ganze Ticket wiederholen.
 * Verglichen wird über die ID, wo es eine gibt — Namen stehen je Quelle leicht anders da.
 * Beschreibung und Kommentare zeigt die Vorschau als eigene Blöcke.
 */
export function ticketChangeRows(
  kind: KianAction['kind'],
  item: Pick<KianActionItem, 'resolved'>,
  current: KianTicketCurrent | null,
): KianChangeRow[] {
  const r = item.resolved ?? {};
  const t = r.ticket ?? null;
  const create = kind === 'TICKET_CREATE';
  const rows: KianChangeRow[] = [];
  const push = (
    field: KianChangeRow['field'],
    label: string,
    after: string | null | undefined,
    before: string | null | undefined,
    same: boolean,
  ) => {
    if (!after) return;
    if (create) {
      rows.push({ field, label, before: null, beforeIsValue: false, after });
      return;
    }
    if (same) return;
    rows.push({ field, label, before: before || '—', beforeIsValue: !!before && before !== NOBODY, after });
  };
  const eqId = (a: string | null | undefined, b: string | null | undefined) => !!a && !!b && String(a) === String(b);
  const eqText = (a: string | null | undefined, b: string | null | undefined) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

  if (r.customer) {
    const name = r.customer.number ? `${r.customer.name} (${r.customer.number})` : r.customer.name;
    const partyId = current?.partyId ?? t?.partyId;
    push('customer', 'Kunde', name, current?.partyName ?? t?.partyName, eqId(partyId, r.customer.id));
  }
  if (r.subject) {
    const before = current?.subject ?? t?.subject;
    push('subject', 'Betreff', r.subject, before, (before ?? '').trim() === r.subject.trim());
  }
  if (r.status) {
    const id = current?.statusId ?? t?.statusId;
    const name = current?.status ?? t?.statusName;
    push('status', 'Status', r.status.name, name, eqId(id, r.status.id) || (!id && eqText(name, r.status.name)));
  }
  if (r.priority) {
    push('priority', 'Priorität', r.priority.name, current?.priority,
      eqId(current?.priorityId, r.priority.id) || (!current?.priorityId && eqText(current?.priority, r.priority.name)));
  }
  if (r.category) {
    push('category', 'Kategorie', r.category.name, current?.category,
      eqId(current?.categoryId, r.category.id) || (!current?.categoryId && eqText(current?.category, r.category.name)));
  }
  const assigneeId = current?.assigneeId ?? t?.assigneeId ?? null;
  const assigneeName = current?.assignee ?? t?.assigneeName ?? null;
  if (r.unassign) {
    if (create || assigneeId || assigneeName) push('assignee', 'Bearbeiter', 'niemand (zurück in den Pool)', assigneeName, false);
  } else if (r.assignee) {
    const after = r.assignee.isSelf ? `${r.assignee.name} (du)` : r.assignee.name;
    push('assignee', 'Bearbeiter', after, assigneeName ?? NOBODY, eqId(assigneeId, r.assignee.id));
  }
  if (r.dueDate) {
    const before = germanDate(current?.dueDate);
    const after = germanDate(r.dueDate);
    push('dueDate', 'Fällig am', after, before, before === after);
  }
  return rows;
}

/**
 * Bearbeiter-Auswahl in Entwürfen: ServiceHub-Techniker mit eigenem weclapp-Benutzer — dieselbe
 * Liste, gegen die der Server auflöst (`loadResolveContext`). Nicht die rohe weclapp-Liste: die
 * enthält API-, System- und Alt-Konten (etwa „weclapp Support", den weclapp als Superuser
 * ablehnt). `local:`-Techniker kennt der KIAN-Executor noch nicht.
 */
export function kianAssigneeOptions(technicians: PortalTechnician[] | undefined): Array<{ value: string; label: string }> {
  return (technicians ?? [])
    .filter((t) => t.weclappUserId && !t.weclappUserId.startsWith('local:'))
    .map((t) => ({ value: String(t.weclappUserId), label: t.displayName }));
}
